import { TextContentElement } from "@deltek/specification-client/core/Models/textContentElement"
import { UnitsOfMeasure, UnitsOfMeasureKey } from "models/projectSettings"
import { normalizeSpaces } from "shared/utils/stringUtils"
import { ELEMENT_TYPE } from "../constants/elementTypes"

/**
 * Returns true if the content item contains a UnitsOfMeasure element with
 * at least one non-empty imperial or metric value.
 */
export const hasUOMContent = (contentItem: Partial<TextContentElement>): boolean => {
    return (
        contentItem.elements?.some(
            (el) =>
                el.type === ELEMENT_TYPE.UNITS_OF_MEASURE
        ) ?? false
    )
}

/**
 * Returns true if currentContent matches the original UOM value on the element,
 * comparing against the field that is active under the current unit of measure setting.
 *
 * - IMPERIAL → compares against imperialUnits
 * - METRIC   → compares against metricUnits
 * - BOTH     → compares against "imperial (metric)" combined string
 */
export const hasUOMContentMatchesSetting = (
    currentContent: string,
    contentItem: Partial<TextContentElement>,
    unitOfMeasureSetting: UnitsOfMeasure
): boolean => {
    const uomElement = contentItem.elements?.find(
        (el) => el.type === 'UnitsOfMeasure'
    )

    if (!uomElement) {
        return false
    }

    const normalizedCurrent = normalizeSpaces(currentContent.trim())

    switch (unitOfMeasureSetting.toString()) {
        case UnitsOfMeasureKey.IMPERIAL:
            return (
                normalizedCurrent === normalizeSpaces(uomElement.imperialUnits?.trim() ?? '')
            )

        case UnitsOfMeasureKey.METRIC:
            return (
                normalizedCurrent === normalizeSpaces(uomElement.metricUnits?.trim() ?? '')
            )

        case UnitsOfMeasureKey.BOTH: {
            const imperial = normalizeSpaces(uomElement.imperialUnits?.trim() ?? '')
            const metric = normalizeSpaces(uomElement.metricUnits?.trim() ?? '')
            // Matches the "imperial (metric)" pattern produced by processUOMElement
            const combined = metric ? `${imperial} (${metric})` : imperial
            return normalizedCurrent === combined
        }

        default:
            return false
    }
}
